<template>
  <Component
    :is="$isMobile ? 'UniversalSearchMobile' : 'BaseModal'"
    v-bind="wrapperProps"
    data-testid="universal-search-modal"
    @close="close"
    @keydown.esc="onEscape"
  >
    <template #header>
      <h2 class="tw:sr-only">
        {{ $t("search_placeholder") }}
      </h2>

      <UniversalSearchInput
        ref="input"
        v-model="searchOptions.dummyStream.keywords"
        v-model:scope="searchOptions.searchScope"
        @clear="reset"
        @close="close"
        @focus="isSearchFocused = true"
        @input="onInput"
        @search="debouncedOnSearch"
      />
    </template>

    <UniversalSearchRecentSearches
      v-if="showRecentSearches"
      :query="typedQuery"
      @focus-search="focusInput"
      @select="onSelect"
    />

    <div
      v-if="showResults"
      class="results row"
    >
      <div
        class="q-px-md q-py-sm"
        :class="[
          $isDesktop && 'column scroll-y full-height no-wrap col-8',
          $isMobile && 'bg-body search-results-container',
          $q.platform.is.ios && 'full-height',
        ]"
      >
        <div
          v-if="$isMobile"
          class="q-mb-sm controls-container items-center"
        >
          <UniversalSearchActionsheetFilters
            v-model:range="searchOptions.range"
            v-model:sources="searchOptions.sources"
            v-model:stream="searchOptions.dummyStream"
            v-model:streams="searchOptions.streams"
            :excluded-media="excludedMedia"
            :search-scope="searchOptions.searchScope"
            @search="debouncedOnSearch"
          />
        </div>

        <MentionStream
          ref="mentionStream"
          class="scroll-y stream"
          :default-range="searchOptions.range"
          :fetching-options="fetchingOptions"
          :fetching-service="fetchMentions"
          hide-button-bar
          hide-edit-button
          hide-filters-selector
          hide-generate-report-button
          hide-header
          hide-notification-watcher
          hide-range-selector
          hide-stream-visualisation-button
          no-highlighting
          paginate
          :stream="searchOptions.dummyStream"
        >
          <template #noMentions>
            <div class="no-media-container column items-center justify-center">
              <i18n-t
                class="q-mb-md text-center"
                keypath="universal_search.no_coverage_try_list"
                scope="global"
                tag="strong"
              >
                <template #lineBreak><br /></template>
              </i18n-t>
            </div>
          </template>
        </MentionStream>
      </div>

      <div
        v-if="$isDesktop && !filtersHidden"
        class="filters-container full-height scroll-y col-4"
      >
        <div class="q-px-md q-py-sm flex justify-between">
          <span class="title bold">{{
            $t("universal_search.filter_options")
          }}</span>

          <QBtn
            dense
            flat
            :label="$t('universal_search.reset')"
            size="12px"
            style="font-weight: normal"
            @click="resetFilters"
          />
        </div>
        <div
          v-if="searchOptions.searchScope === 'streams'"
          class="q-pa-md"
        >
          <span class="bold row text-16">{{
            $t("universal_search.mention_streams_group")
          }}</span>
          <StreamsByGroupPicker
            v-model="searchOptions.streams"
            v-model:selected-group="selectedGroup"
            @update:model-value="debouncedOnSearch"
          />
        </div>

        <div
          v-if="hasSearchFilters"
          class="q-pa-md"
        >
          <span class="bold text-16 block">{{ $t("global.date_range") }}</span>
          <div class="flex items-center">
            <QBtn
              dense
              flat
              icon="navigate_before"
              @click="previousDay()"
            />
            <BaseInputDatePicker
              v-model:range="searchOptions.range"
              close-on-update
              date-style="long"
              limit-to-past-year
              range-selection="custom"
              show-date-input
              show-range-options
              show-time-input
              show-update-button
              @update:range="debouncedOnSearch"
            >
              <template #btn="{ toggle, afterLabel, beforeLabel }">
                <div
                  class="pointer bigger flex items-center justify-center"
                  @click="toggle"
                >
                  <QIcon
                    class="q-mr-xs"
                    name="fas fa-calendar"
                    size="14px"
                  />
                  {{
                    $t("universal_search.after_to_before", {
                      afterLabel,
                      beforeLabel,
                    })
                  }}
                </div>
              </template>
            </BaseInputDatePicker>
            <QBtn
              dense
              flat
              icon="navigate_next"
              @click="nextDay()"
            />
          </div>
        </div>

        <div
          v-if="$isDesktop"
          class="q-pa-md"
        >
          <span class="bold text-16 block">{{ $t("global.media_types") }}</span>
          <MediumSelector
            v-model="searchOptions.dummyStream"
            checkbox-class="tw:basis-1/3"
            :excluded-media="excludedMedia"
            @update:model-value="debouncedOnSearch"
          />
        </div>
        <div
          v-if="$isDesktop"
          class="q-pa-md"
        >
          <span class="q-pb-sm bold text-16 block">{{
            $t("universal_search.sources")
          }}</span>
          <FiltersAutocomplete
            v-model="searchOptions.sources"
            filter-type="sources"
            keep-expanded
            :media="searchEnabledMedia"
            :stream="searchOptions.dummyStream"
            @update:model-value="debouncedOnSearch"
          />
        </div>
      </div>
    </div>
  </Component>
</template>

<script>
import { debounce } from "lodash-es";
import { storeToRefs } from "pinia";

import BaseInputDatePicker from "shared/components/base/BaseInputDatePicker";
import FiltersAutocomplete from "shared/components/core/forms/FiltersAutocomplete.vue";
import BaseModal from "shared/components/core/modals/BaseModal.vue";
import StreamsByGroupPicker from "shared/components/core/pickers/StreamsByGroupPicker.vue";
import MediumSelector from "shared/components/selectors/MediumSelector";
import useContentWarning from "shared/composables/useContentWarning";
import {
  addTime,
  subtractTime,
  toMilliseconds,
  unixDateTime,
} from "shared/helpers/date";
import DateRange from "shared/helpers/DateRange";
import { getMediaForStream, getSources } from "shared/helpers/media";
import { sourceGroupKeys, sourceKeysFor } from "shared/helpers/sourceFilters";
import MentionStream from "shared/modules/MentionStream/components/MentionStream/MentionStream.vue";
import { fetchMentions } from "shared/services/fetch/fetchMentions";
import { useStreamsStore } from "shared/stores/streams";
import { useUniversalSearchStore } from "shared/stores/universalSearch";
import { useUserStore } from "shared/stores/user";

import UniversalSearchActionsheetFilters from "./UniversalSearchActionsheetFilters.vue";
import UniversalSearchInput from "./UniversalSearchInput.vue";
import UniversalSearchMobile from "./UniversalSearchMobile.vue";
import UniversalSearchRecentSearches from "./UniversalSearchRecentSearches.vue";

export default {
  name: "UniversalSearchModal",
  components: {
    BaseInputDatePicker,
    MentionStream,
    FiltersAutocomplete,
    BaseModal,
    UniversalSearchActionsheetFilters,
    UniversalSearchInput,
    UniversalSearchRecentSearches,
    UniversalSearchMobile,
    MediumSelector,
    StreamsByGroupPicker,
  },
  setup() {
    const { openSearchCoverageContentWarningModal } = useContentWarning();

    const streamsStore = useStreamsStore();
    const { primaryStreams } = storeToRefs(streamsStore);

    const { groupedTraditionalStreams } = streamsStore;

    const universalSearchStore = useUniversalSearchStore();

    const { searchOpen, searchQuery, searchScope } =
      storeToRefs(universalSearchStore);

    const { hideSearch, setSearchQuery, setSearchScope } = universalSearchStore;

    const userStore = useUserStore();
    const { adminUserEnabled, isLoggedIn } = storeToRefs(userStore);

    return {
      openSearchCoverageContentWarningModal,
      groupedTraditionalStreams,
      primaryStreams,

      hideSearch,
      setSearchQuery,
      setSearchScope,
      searchOpen,
      searchQuery,
      searchScope,

      adminUserEnabled,
      isLoggedIn,
    };
  },
  data() {
    return {
      fetchMentions,
      sources: getSources(),
      searchOptions: {
        searchScope: "streams",
        streams: [],
        sources: [],
        range: DateRange.today(),
        dummyStream: {
          skipReload: true, // so watcher in MentionStream.vue doesn't trigger redundant refresh
          keywords: "",
        },
      },
      defaultDateRange: DateRange.today(),
      isSearching: false,
      isSearchFocused: false,
      typedQuery: "",
      savedSearch: {},
      debouncedOnSearch: debounce(this.onSearch, 400),
      searchHidden: false,
      filtersHidden: false,
      selectedGroup: null,
    };
  },
  computed: {
    storedSearchScope: {
      get() {
        return this.searchScope;
      },
      set(value) {
        this.setSearchScope(value);
      },
    },
    storedSearchQuery: {
      get() {
        return this.searchQuery;
      },
      set(value) {
        this.setSearchQuery(value);
      },
    },
    showResults() {
      return this.searchOptions.dummyStream.keywords.length && this.isSearching;
    },
    showRecentSearches() {
      return this.isSearchFocused && !this.showResults;
    },
    wrapperProps() {
      // Mobile stays mounted for the life of the app, so it hides rather than
      // closes: while another modal is up, or while the route has moved off
      // /search, the query and results survive underneath.
      if (this.$isMobile) {
        return {
          hidden: this.searchHidden || this.$modals.hasModalOpen(),
        };
      }

      return {
        // `!` because the shared container caps itself at 500px and centres
        // itself, both at a specificity a bare utility loses to.
        containerClass:
          "tw:max-w-[1000px]! tw:items-start! tw:pt-[calc(7vh_-_16px)]",
        contentClass:
          "tw:min-h-0 tw:flex-1 tw:overflow-hidden tw:rounded-b-[5px] tw:p-0",
        headerClass: "",
        hideCloseButton: true,
        modalClass: this.showResults ? "tw:h-[86vh]" : "",
      };
    },
    hasSearchFilters() {
      return ["streams", "all"].includes(this.searchOptions.searchScope);
    },
    canSearchAvailableContent() {
      if (this.adminUserEnabled) return true;

      return (
        this.$features.has("has_search_available_content") &&
        this.$permissions.has("can_search_available_content")
      );
    },
    selectedStreamIds() {
      return this.searchOptions.streams.map((stream) => stream.id);
    },
    selectedContentTypes() {
      return this.sources.reduce(
        (acc, source) => ({
          ...acc,
          [`${source.field}_content`]:
            this.searchOptions.dummyStream[`${source.field}_content`],
        }),
        {}
      );
    },
    excludedMedia() {
      // No social for Journalists
      if (this.searchOptions.searchScope === "journalists") {
        return ["Social"];
      }

      return [];
    },
    searchEnabledMedia() {
      return getMediaForStream(this.searchOptions.dummyStream);
    },
    endpoint() {
      return {
        all: "search",
        streams: "search/streams",
        journalists: "search/journalists",
      }[this.searchOptions.searchScope];
    },
    fetchingOptions() {
      const { selectedStreamIds, endpoint, excludedMedia } = this;
      const { range, sources } = this.searchOptions;

      return {
        stream_ids: selectedStreamIds,
        endpoint,
        range,
        excludedMedia,
        useMilliseconds: true,
        source_group_keys: sourceGroupKeys(sources),
        source_keys: sourceKeysFor(sources),
      };
    },
    shouldResetSavedSearch() {
      return (
        this.savedSearch.query !== this.searchOptions.dummyStream.keywords ||
        this.savedSearch.search_type !== this.searchOptions.searchScope
      );
    },
    streamGroups() {
      return this.groupedTraditionalStreams({ menuGroups: true });
    },
  },
  watch: {
    $route() {
      // On mobile the search has its own route, and leaving it only hides the
      // search — coming back restores the query and results as they were.
      if (this.$isMobile) {
        this.searchHidden = this.$route.name !== "search";

        return;
      }

      this.close();
    },
    isLoggedIn() {
      if (!this.isLoggedIn) {
        this.close();
      }
    },
    searchOpen(isOpen) {
      if (this.$isMobile && isOpen) this.onOpen();
    },
  },
  mounted() {
    // Desktop mounts the search when it opens; mobile keeps it mounted for the
    // life of the app, so there the `searchOpen` watcher is what opens it.
    if (this.$isMobile) {
      this.init();

      return;
    }

    this.onOpen();
  },
  beforeUnmount() {
    this.debouncedOnSearch.cancel();

    this.hideSearch();

    this.previouslyFocusedElement?.focus();
  },
  methods: {
    async onOpen() {
      this.previouslyFocusedElement =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;

      this.init();

      if (this.storedSearchQuery) {
        Object.assign(this.searchOptions.dummyStream, {
          keywords: this.storedSearchQuery,
        });

        this.debouncedOnSearch();
        this.storedSearchQuery = "";

        return;
      }

      await this.$nextTick();

      this.focusInput();
    },
    focusInput() {
      this.$refs.input?.focus();
    },
    init() {
      this.sources = getSources();

      Object.assign(this.searchOptions, {
        streams: this.primaryStreams,
        searchScope: this.canSearchAvailableContent
          ? "all"
          : this.storedSearchScope,
      });

      this.reset();
    },
    onEscape(event) {
      // The scope dropdown swallows Escape to close its own listbox first.
      if (event.defaultPrevented) return;

      this.close();
    },
    close() {
      this.reset();
      this.hideSearch();

      if (this.$isMobile) {
        // Mobile search is never unmounted, so closing has to undo the route
        // push that opened it and hand focus back itself.
        if (this.$route.name === "search") this.$router.go(-1);

        this.previouslyFocusedElement?.focus();
      }

      this.$track("Closed Universal Search");
    },
    reset() {
      this.filtersHidden = false;
      this.isSearching = false;
      this.typedQuery = "";
      this.searchOptions.dummyStream.keywords = "";
      this.resetFilters();

      this.$track("Reset Universal Search");
    },
    resetFilters() {
      this.storedSearchScope = "streams";

      this.searchOptions = {
        ...this.searchOptions,
        range: this.defaultDateRange,
        streams: this.primaryStreams,
        sources: [],
      };

      this.resetSelectedGroup();

      const stream = { ...this.searchOptions.dummyStream };

      this.sources.forEach((source) => {
        stream[`${source.field}_content`] = true;
      });

      this.searchOptions.dummyStream = stream;

      this.$track("Reset Universal Search Filters");

      this.debouncedOnSearch();
    },
    resetSelectedGroup() {
      this.selectedGroup = {
        ...this.streamGroups.find(
          ({ slug }) => slug === this.$route.params.groupSlug
        ),
      };
    },
    onInput(query) {
      this.typedQuery = query;
    },
    onSelect(search) {
      this.savedSearch = search;
      this.isSearchFocused = false;

      this.searchOptions.dummyStream = {
        ...this.searchOptions.dummyStream,
        ...this.savedSearch,
        id: null,
        keywords: this.savedSearch.query,
      };

      this.searchOptions = {
        ...this.searchOptions,
        range: new DateRange({
          before: this.savedSearch.before,
          after: this.savedSearch.after,
        }),

        searchScope: this.savedSearch.search_type,

        streams: this.searchOptions.streams,
      };

      this.isSearching = true;

      this.$track("Selected Saved Universal Search", {
        searchId: search.id,
      });
    },
    async onSearch() {
      if (!this.searchOptions.dummyStream.keywords) return;

      if (this.shouldResetSavedSearch) this.savedSearch = {};

      if (
        this.savedSearch.search_type === "streams" &&
        this.savedSearch.print_content
      ) {
        await this.openSearchCoverageContentWarningModal();
      }

      this.isSearching = true;
      this.refreshMentions(true);
    },
    saveSearch(params) {
      const handleErrors = true;

      return this.savedSearch.id
        ? this.$streemApiV1.put(`universal_searches/${this.savedSearch.id}`, {
            params,
            handleErrors,
          })
        : this.$streemApiV1.post("universal_searches", {
            params,
            handleErrors,
          });
    },
    async recordSearch() {
      const params = {
        query: this.searchOptions.dummyStream.keywords,
        stream_ids: this.selectedStreamIds,
        search_type: this.searchOptions.searchScope,
        ...this.searchOptions.range,
        ...this.selectedContentTypes,
      };

      const result = await this.saveSearch(params);
      this.savedSearch = result.data;
    },
    async refreshMentions(shouldTrackUniversalSearch = false) {
      if (!this.$refs.mentionStream) await this.$nextTick();

      if (this.$refs.mentionStream) {
        this.$refs.mentionStream.refreshMentions({
          ...(shouldTrackUniversalSearch
            ? { shouldTrackUniversalSearch: true }
            : {}),
        });
      }

      if (this.$refs.journalistsList) {
        this.$refs.journalistsList.refreshJournalists();
      }

      this.recordSearch();
    },
    previousDay() {
      ["before", "after"].forEach((boundary) => {
        this.searchOptions.range[boundary] = unixDateTime(
          subtractTime(
            new Date(toMilliseconds(this.searchOptions.range[boundary])),
            1,
            "day"
          )
        );
      });

      this.debouncedOnSearch();
    },
    nextDay() {
      ["before", "after"].forEach((boundary) => {
        this.searchOptions.range[boundary] = unixDateTime(
          addTime(
            new Date(toMilliseconds(this.searchOptions.range[boundary])),
            1,
            "day"
          )
        );
      });

      this.debouncedOnSearch();
    },
  },
};
</script>

<style lang="scss" scoped>
.text-16 {
  font-size: 16px;
}

.results,
.search-results-container {
  padding: 8px 4px;
}

.results {
  height: 100%;
}

.title {
  font-size: 19px;
}

.mobile {
  .controls-container {
    height: 40px;
  }

  .results {
    flex-wrap: nowrap;
  }

  .search-results-container {
    position: initial;
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 0;
  }

  .stream {
    flex: 1;
    min-height: 0;
    padding-bottom: env(safe-area-inset-bottom);
    overflow: auto;
  }
}
</style>
